import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './Generate.css';
import GenButton from './GenButton';
import './GenButton.css';
import GenConfig from '../../core/GenConfig';
import { OpenInNew, Tune as TuneIcon } from '@mui/icons-material';
import GenTimer, { GenTimerSheetRow } from './GenTimer';
import GenConfPanel from './GenConfPanel';
import { useOverlay } from '../../contexts/OverlayContext';
import Tooltip from '../utils/Tooltip';
import GenerationRunControl from './GenerationRunControl';
import GeneratorControlBar from './GeneratorControlBar';

const TXT_GEN_SETTINGS = { fr: 'Param\u00e8tres avanc\u00e9s', en: 'Advanced settings', de: 'Erweiterte Einstellungen', es: 'Ajustes avanzados', pt: 'Configurações avançadas' };
const TXT_GENERATOR_SETTINGS = { fr: 'Param\u00e8tres avanc\u00e9s', en: 'Advanced settings', de: 'Erweiterte Einstellungen', es: 'Ajustes avanzados', pt: 'Configurações avançadas' };
const TXT_GEN_BUTTON_TOOLTIP_CRIT_SINGULAR = { fr: 'crit\u00e8re', en: 'criterion', de: 'Kriterium', es: 'criterio', pt: 'critério' };
const TXT_GEN_BUTTON_TOOLTIP_CRIT_PLURAL = { fr: 'crit\u00e8res', en: 'criteria', de: 'Kriterien', es: 'criterios', pt: 'critérios' };
const TXT_GEN_BUTTON_TOOLTIP_SEC_SINGULAR = { fr: 'seconde', en: 'second', de: 'Sekunde', es: 'segundo', pt: 'segundo' };
const TXT_GEN_BUTTON_TOOLTIP_SEC_PLURAL = { fr: 'secondes', en: 'seconds', de: 'Sekunden', es: 'segundos', pt: 'segundos' };
const TXT_GEN_BUTTON_TOOLTIP_LINE = {
    fr: 'G\u00e9n\u00e9rer le stuff pour {n} {crit}, {s} {sec}',
    en: 'Generate build for {n} {crit}, {s} {sec}',
    de: 'Build generieren für {n} {crit}, {s} {sec}',
    es: 'Generar la build para {n} {crit}, {s} {sec}',
    pt: 'Gerar a build para {n} {crit}, {s} {sec}',
};

const TXT_GEN_BUTTON_TOOLTIP_SCORE = { fr: 'Score', en: 'Score', de: 'Score', es: 'Puntuación', pt: 'Pontuação' };

/** @param {number} n @param {number} s @param {string} lang */
function formatGenButtonTooltip(n, s, lang) {
    const nc = Math.max(0, Math.floor(Number(n)) || 0);
    const sc = Math.max(0, Math.floor(Number(s)) || 0);
    const crit =
        nc === 1
            ? TXT_GEN_BUTTON_TOOLTIP_CRIT_SINGULAR[lang] || TXT_GEN_BUTTON_TOOLTIP_CRIT_SINGULAR.en
            : TXT_GEN_BUTTON_TOOLTIP_CRIT_PLURAL[lang] || TXT_GEN_BUTTON_TOOLTIP_CRIT_PLURAL.en;
    const sec =
        sc === 1
            ? TXT_GEN_BUTTON_TOOLTIP_SEC_SINGULAR[lang] || TXT_GEN_BUTTON_TOOLTIP_SEC_SINGULAR.en
            : TXT_GEN_BUTTON_TOOLTIP_SEC_PLURAL[lang] || TXT_GEN_BUTTON_TOOLTIP_SEC_PLURAL.en;
    const tpl = TXT_GEN_BUTTON_TOOLTIP_LINE[lang] || TXT_GEN_BUTTON_TOOLTIP_LINE.en;
    return tpl
        .replace('{n}', String(nc))
        .replace('{crit}', crit)
        .replace('{s}', String(sc))
        .replace('{sec}', sec);
}

const CONTROLS_LAYOUT_MOBILE = 'mobile';
const CONTROLS_LAYOUT_DESKTOP = 'desktop';

const Generate = ({
    onGenerate,
    onStop,
    genConfig,
    setGenConfig,
    status,
    localSolve = false,
    disabled,
    lang = 'fr',
    build,
    scoreValue,
    scoreColor,
    liveSoftScore = null,
    liveMaximizeScore = null,
    controlsOnly = false,
    controlsLayout = CONTROLS_LAYOUT_MOBILE,
    headerRightOnly = false,
    centerContent,
    layout = 'default', // 'default' | 'inlineBar' | 'generatorBar'
    invalidBuildReason = null,
    showGeneratorControls = true,
    displayMode = 'generator',
    displayModeToggle = null,
    displayModeTitle = null,
    defenseControls = null,
    fmControl = null,
    criteriaContent = null,
    scoreContent = null,
    sheetSummaryContent = null,
    sheetDefenseContent = null,
    sheetFmContent = null,
    sheetScoreContent = null,
    sheetCriteriaContent = null,
}) => {
    const [, setIsConfExpanded] = useState(false);
    const [hasUserAdjusted, setHasUserAdjusted] = useState(false);
    const { showOverlay } = useOverlay();

    const activeStatsCount = useMemo(() => {
        if (!build) return 0;
        return typeof build.getCombinedGeneratorTargetCount === 'function'
            ? build.getCombinedGeneratorTargetCount()
            : build.getSolverTargetCount();
    }, [build]);

    const spellCount = useMemo(() => {
        if (!build || typeof build.getSpellSolverTargetCount !== 'function') return 0;
        return build.getSpellSolverTargetCount();
    }, [build]);

    const weaponCount = useMemo(() => {
        if (!build || typeof build.getWeaponSolverTargetCount !== 'function') return 0;
        return build.getWeaponSolverTargetCount();
    }, [build]);

    const defenseShare = genConfig?.defenseShare ?? 0;

    const level = useMemo(() => {
        if (!build) return 0;
        return Number(typeof build.getLvl === 'function' ? build.getLvl() : build.lvl) || 0;
    }, [build]);

    // Light base from criterion count, then a few step bumps for weapons /
    // defense and a step down for low levels — no hard floors (see
    // GenConfig.suggestTimeLimit). spellCount is passed but not yet weighted.
    const calculatedTimeLimit = useMemo(
        () => GenConfig.suggestTimeLimit({
            criteriaCount: activeStatsCount,
            spellCount,
            weaponCount,
            bestWeapon: build?.maximizeBestWeapon === true,
            defenseShare,
            level,
        }),
        [activeStatsCount, spellCount, weaponCount, defenseShare, level, build?.maximizeBestWeapon],
    );

    // Reset manual override when the suggestion inputs change.
    useEffect(() => {
        setHasUserAdjusted(false);
    }, [activeStatsCount, spellCount, weaponCount, defenseShare, level, build?.maximizeBestWeapon]);

    // Update genConfig timeLimit when calculatedTimeLimit changes (only if user hasn't manually adjusted)
    useEffect(() => {
        if (!disabled && !hasUserAdjusted && calculatedTimeLimit !== genConfig.timeLimit) {
            setGenConfig(prev => prev.update('timeLimit', calculatedTimeLimit));
        }
    }, [calculatedTimeLimit, genConfig.timeLimit, setGenConfig, hasUserAdjusted, disabled]);

    const handleGenerateClick = useCallback(() => {
        // Collapse the configuration panel when generate is clicked
        setIsConfExpanded(false);
        onGenerate();
    }, [onGenerate]);

    const handleOpenSettings = useCallback(() => {
        showOverlay([
            <h6>{TXT_GEN_SETTINGS[lang]}</h6>,
            <GenConfPanel
                lang={lang}
                genConfig={genConfig}
                setGenConfig={setGenConfig}
                build={build}
                isExpanded={true}
                setIsExpanded={() => { }}
            />
        ]);
    }, [showOverlay, genConfig, setGenConfig, lang, build]);

    const genButtonTooltip = useMemo(() => {
        const wallS = genConfig?.timeLimit ?? GenConfig.TIME_LIMIT_DEFAULT;
        const base = formatGenButtonTooltip(
            activeStatsCount,
            wallS,
            lang,
        );
        if (liveSoftScore != null && Number.isFinite(liveSoftScore)) {
            const scoreWord = TXT_GEN_BUTTON_TOOLTIP_SCORE[lang] || TXT_GEN_BUTTON_TOOLTIP_SCORE.en;
            return `${base} | ${scoreWord} ${Math.round(liveSoftScore)}`;
        }
        return base;
    }, [activeStatsCount, genConfig?.timeLimit, lang, liveSoftScore]);

    const renderTimeInput = ({ sheet = false } = {}) => (
        <GenTimer
            value={genConfig.timeLimit}
            onChange={(newValue) => {
                setGenConfig(prev => prev.update('timeLimit', newValue));
                setHasUserAdjusted(true);
            }}
            min={GenConfig.TIME_LIMIT_MIN}
            max={GenConfig.TIME_LIMIT_MAX}
            step={GenConfig.TIME_LIMIT_STEP}
            steps={GenConfig.TIME_LIMIT_STEPS}
            disabled={disabled || status === 'queued' || status === 'booting' || status === 'initializing' || status === 'loading'}
            lang={lang}
            showIcon={!sheet}
            className={sheet ? 'gen-timer--sheet' : ''}
        />
    );

    const isGenBusy =
        status === 'queued' || status === 'booting' || status === 'initializing' || status === 'loading';
    const hasBlockingInvalidBuild = !isGenBusy && typeof invalidBuildReason === 'string' && invalidBuildReason.trim().length > 0;

    const renderButton = ({ forceStopVisual = false, noIcon = false, iconOnly = false } = {}) => (
        <Tooltip
            content={isGenBusy ? null : genButtonTooltip}
            wrapperClassName="generate-gen-button-tooltip-wrap"
        >
            <GenButton
                onClick={handleGenerateClick}
                onStop={onStop}
                status={status}
                disabled={disabled}
                hasTargets={activeStatsCount > 0}
                invalidBuildReason={invalidBuildReason}
                lang={lang}
                timeLimit={genConfig.timeLimit}
                scoreValue={scoreValue}
                liveSoftScore={liveSoftScore}
                liveMaximizeScore={liveMaximizeScore}
                forceStopVisual={forceStopVisual}
                noIcon={noIcon}
                iconOnly={iconOnly}
            />
        </Tooltip>
    );

    const renderSettingsButton = () => (
        <Tooltip content={TXT_GENERATOR_SETTINGS[lang] || TXT_GENERATOR_SETTINGS['en']}>
            <button
                className="settings-btn row"
                onClick={handleOpenSettings}
                disabled={disabled || status === 'queued' || status === 'booting' || status === 'initializing' || status === 'loading'}
            >
                <TuneIcon fontSize="medium" />
                <span className="settings-btn__label">
                    {TXT_GENERATOR_SETTINGS[lang] || TXT_GENERATOR_SETTINGS.en}
                </span>
            </button>
        </Tooltip>
    );

    // Mobile sheet: the last row of the list (icon, label, open button).
    const renderSettingsSheetRow = () => {
        const label = TXT_GENERATOR_SETTINGS[lang] || TXT_GENERATOR_SETTINGS.en;
        return (
            <div className="gen-settings-sheet-row row">
                <span className="mobile-sheet-list__icon-slot">
                    <TuneIcon className="gen-settings-sheet-row__icon" fontSize="small" />
                </span>
                <span className="mobile-sheet-list__label">{label}</span>
                <button
                    type="button"
                    className="gen-settings-sheet-row__open"
                    onClick={handleOpenSettings}
                    disabled={disabled || isGenBusy}
                    aria-label={label}
                >
                    <OpenInNew fontSize="small" />
                </button>
            </div>
        );
    };

    const renderControls = () => (
        <div className="generate-right-group row">
            <div className="generate-bar-start row">
                {renderTimeInput()}
                {centerContent}
            </div>
            <div className="generate-bar-center">
                {renderButton()}
            </div>
            <div className="generate-bar-end row">
                {renderSettingsButton()}
            </div>
        </div>
    );

    if (layout === 'generatorBar') {
        const isBusy = isGenBusy;
        const isBlocked = hasBlockingInvalidBuild;
        const isCollapsed = displayMode === 'simple';

        const runControlProps = {
            status,
            localSolve,
            lang,
            timeLimit: genConfig.timeLimit,
            onGenerate: handleGenerateClick,
            onStop,
            disabled,
            hasTargets: activeStatsCount > 0,
            invalidBuildReason,
            tooltipContent: genButtonTooltip,
            showBlockedStatus: isBlocked,
            className: 'generator-bottom-bar__run-control',
            statusClassName: 'generator-bottom-bar__busy-fill',
            buttonClassName: 'generator-bottom-bar__generate',
        };
        const runControl = <GenerationRunControl {...runControlProps} />;
        const sheetRunStatusContent = isBusy ? (
            <GenerationRunControl {...runControlProps} showButton={false} statusLayout="sheet" />
        ) : null;
        const sheetRunButtonContent = (
            <GenerationRunControl
                {...runControlProps}
                showStatus={false}
                showBlockedStatus={false}
            />
        );
        const sheetTimer = (
            <GenTimerSheetRow lang={lang} disabled={isBusy}>
                {renderTimeInput({ sheet: true })}
            </GenTimerSheetRow>
        );

        return (
            <GeneratorControlBar
                variant="generator"
                isBusy={isBusy}
                isBlocked={isBlocked}
                isCollapsed={isCollapsed}
                showGeneratorControls={showGeneratorControls}
                displayModeToggle={displayModeToggle}
                displayModeTitle={displayModeTitle}
                timerContent={renderTimeInput()}
                genModeControls={defenseControls}
                settingsContent={(
                    <>
                        {fmControl}
                        {renderSettingsButton()}
                    </>
                )}
                sheetSettingsContent={renderSettingsSheetRow()}
                criteriaContent={criteriaContent}
                scoreContent={scoreContent}
                runControlContent={runControl}
                sheetRunStatusContent={sheetRunStatusContent}
                sheetRunButtonContent={sheetRunButtonContent}
                sheetSummaryContent={sheetSummaryContent}
                sheetTimerContent={sheetTimer}
                sheetDefenseContent={sheetDefenseContent}
                sheetFmContent={sheetFmContent}
                sheetScoreContent={sheetScoreContent}
                sheetCriteriaContent={sheetCriteriaContent}
            />
        );
    }

    if (headerRightOnly) {
        return (
            <div className="generate generate-row row">
                {renderControls()}
            </div>
        );
    }

    if (controlsOnly) {
        if (controlsLayout === CONTROLS_LAYOUT_DESKTOP) {
            return (
                <>
                    <div className="generator-header-center generate generate-controls-only row">
                        {renderButton()}
                    </div>
                    <div className="generator-header-right row">
                        {renderTimeInput()}
                        {renderSettingsButton()}
                    </div>
                </>
            );
        }

        return (
            <div className="generator-header-main row">
                <div className="generator-header-targets generator-header-left-controls row">
                    {renderTimeInput()}
                </div>
                <div className="generator-header-center generate generate-controls-only row">
                    {renderButton()}
                </div>
                <div className="generator-header-right row">
                    {renderSettingsButton()}
                </div>
            </div>
        );
    }

    if (layout === 'inlineBar') {
        return (
            <div className="generate-inline-bar">
                {renderTimeInput()}
                {renderButton()}
                {renderSettingsButton()}
            </div>
        );
    }

    return (
        <div className="generate generate-row row">
            {renderControls()}
        </div>
    );
};

export default Generate;
